import React, { useState, useEffect, useMemo, useId } from "react";
import {
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  InputGroup,
  Button,
  Modal,
  Badge,
} from "react-bootstrap";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import LookupLayout from "../layout/LookupLayout";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";

function extractFieldNameFromToken(s) {
  if (!s || typeof s !== "string") return "";
  const t = s.trim();
  const m = t.match(/\{\{\s*([^}]+?)\s*\}\}/);
  return m ? m[1].trim() : t.replace(/[{}]/g, "").trim();
}

function sequenceToRecipients(seq) {
  const users = [];
  const userIds = new Set();
  const fields = [];
  const fieldNames = new Set();
  if (!Array.isArray(seq)) {
    return { hardcodedRecipients: users, recipientFieldTokens: fields };
  }
  for (const item of seq) {
    if (item?.kind === "user" && (item.userId || item.id)) {
      const id = String(item.userId ?? item.id).trim();
      if (id && !userIds.has(id)) {
        userIds.add(id);
        users.push({
          userId: id,
          displayName:
            item.displayName != null && item.displayName !== undefined
              ? String(item.displayName)
              : "",
        });
      }
    } else if (item?.kind === "field" && item.fieldName) {
      const fn = extractFieldNameFromToken(String(item.fieldName));
      if (fn && !fieldNames.has(fn)) {
        fieldNames.add(fn);
        fields.push(fn);
      }
    }
  }
  return { hardcodedRecipients: users, recipientFieldTokens: fields };
}

/**
 * Recipients in add order (left → right). Persists as `recipientSequence`;
 * `hardcodedRecipients` + `recipientFieldTokens` are kept in sync for the API.
 */
export default function WorkflowSendNotification({
  config,
  onChange,
  onConfigPatch,
  objectName,
  readOnly,
}) {
  const [allFields, setAllFields] = useState([]);
  const [pickFieldKey, setPickFieldKey] = useState("");
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [lookupRemountKey, setLookupRemountKey] = useState(0);
  const idPrefix = useId();

  const hardcodedFromConfig = useMemo(() => {
    const byId = new Map();
    const add = (id, displayName) => {
      const s = id != null ? String(id).trim() : "";
      if (!s) return;
      if (!byId.has(s)) {
        byId.set(s, {
          userId: s,
          displayName: displayName != null ? String(displayName) : "",
        });
      }
    };
    if (Array.isArray(config?.hardcodedRecipients)) {
      for (const row of config.hardcodedRecipients) {
        const id = row?.userId ?? row?.id ?? row?.user_id;
        const dn = row?.displayName ?? row?.name ?? row?.display_name ?? "";
        add(id, dn);
      }
    }
    if (Array.isArray(config?.recipientUserIds)) {
      for (const id of config.recipientUserIds) {
        add(id, "");
      }
    }
    if (byId.size > 0) {
      return Array.from(byId.values());
    }
    const leg = config?.recipientUserId;
    if (leg && typeof leg === "string" && !leg.includes("{{")) {
      return [
        {
          userId: leg.trim(),
          displayName: config?.recipientName || "",
        },
      ];
    }
    return [];
  }, [
    config?.hardcodedRecipients,
    config?.recipientUserIds,
    config?.recipientUserId,
    config?.recipientName,
  ]);

  const fieldsFromConfig = useMemo(() => {
    if (
      Array.isArray(config?.recipientFieldTokens) &&
      config.recipientFieldTokens.length > 0
    ) {
      return config.recipientFieldTokens.map((t) =>
        extractFieldNameFromToken(String(t))
      );
    }
    const leg = config?.recipientUserId;
    if (leg && typeof leg === "string" && leg.includes("{{")) {
      return [extractFieldNameFromToken(leg)].filter(Boolean);
    }
    return [];
  }, [config?.recipientFieldTokens, config?.recipientUserId]);

  const recipientSequence = useMemo(() => {
    if (
      Array.isArray(config?.recipientSequence) &&
      config.recipientSequence.length > 0
    ) {
      return config.recipientSequence
        .map((item) => {
          if (item?.kind === "user" && (item.userId || item.id)) {
            return {
              kind: "user",
              userId: String(item.userId ?? item.id).trim(),
              displayName: item.displayName != null ? String(item.displayName) : "",
            };
          }
          if (item?.kind === "field" && item.fieldName) {
            return {
              kind: "field",
              fieldName: extractFieldNameFromToken(String(item.fieldName)),
            };
          }
          return null;
        })
        .filter(Boolean);
    }
    const seq = [];
    for (const u of hardcodedFromConfig) {
      seq.push({
        kind: "user",
        userId: u.userId,
        displayName: u.displayName || "",
      });
    }
    for (const f of fieldsFromConfig) {
      if (f) {
        seq.push({ kind: "field", fieldName: f });
      }
    }
    return seq;
  }, [config?.recipientSequence, hardcodedFromConfig, fieldsFromConfig]);

  const applySequence = (seq) => {
    const reSplit = sequenceToRecipients(seq);
    const patch = {
      recipientSequence: seq,
      hardcodedRecipients: reSplit.hardcodedRecipients,
      recipientFieldTokens: reSplit.recipientFieldTokens,
    };
    const leg = config?.recipientUserId;
    if (leg != null && String(leg).trim() !== "") {
      if (String(leg).includes("{{")) {
        const name = extractFieldNameFromToken(String(leg));
        if (name) {
          const s = new Set(
            reSplit.recipientFieldTokens.map((t) =>
              extractFieldNameFromToken(String(t))
            )
          );
          s.add(name);
          patch.recipientFieldTokens = Array.from(s);
        }
      }
      patch.recipientUserId = "";
      patch.recipientName = "";
    }
    if (onConfigPatch) {
      onConfigPatch(patch);
    } else {
      onChange("recipientSequence", patch.recipientSequence);
      onChange("hardcodedRecipients", patch.hardcodedRecipients);
      onChange("recipientFieldTokens", patch.recipientFieldTokens);
      if (leg != null && String(leg).trim() !== "") {
        onChange("recipientUserId", "");
        onChange("recipientName", "");
      }
    }
  };

  const addHardcodedUser = (userId, displayName) => {
    if (!userId || String(userId).trim() === "") return;
    const id = String(userId).trim();
    if (recipientSequence.some((s) => s.kind === "user" && s.userId === id)) {
      setLookupRemountKey((k) => k + 1);
      return;
    }
    applySequence([
      ...recipientSequence,
      { kind: "user", userId: id, displayName: displayName || "" },
    ]);
    setLookupRemountKey((k) => k + 1);
  };

  const addFieldToken = (fieldName) => {
    if (!fieldName || String(fieldName).trim() === "") return;
    const name = String(fieldName).trim();
    if (recipientSequence.some((s) => s.kind === "field" && s.fieldName === name)) {
      setPickFieldKey("");
      return;
    }
    applySequence([...recipientSequence, { kind: "field", fieldName: name }]);
    setPickFieldKey("");
  };

  const removeAt = (index) => {
    applySequence(recipientSequence.filter((_, i) => i !== index));
  };

  const handleFieldPicked = (field) => {
    if (field?.field_name) {
      addFieldToken(field.field_name);
    }
    setShowFieldSelector(false);
  };

  useEffect(() => {
    const load = async () => {
      if (!objectName) {
        setAllFields([]);
        return;
      }
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
          objectName
        );
        if (response.success && response.data?.fields) {
          setAllFields(response.data.fields);
        } else {
          setAllFields([]);
        }
      } catch (e) {
        console.error("WorkflowSendNotification: failed to load fields", e);
        setAllFields([]);
      }
    };
    load();
  }, [objectName]);

  const userLookupFields = useMemo(() => {
    if (!allFields.length) return [];
    const out = [];
    for (const f of allFields) {
      const dt = (f.data_type || "").toLowerCase();
      const ref = (f.reference_object || "").toLowerCase();
      const isUserRef =
        ref === "user" && (dt === "lookup" || f.is_reference === true);
      if (isUserRef && f.field_name !== "ownerid") {
        out.push(f);
      }
    }
    return out.sort((a, b) =>
      String(a.field_label || a.field_name).localeCompare(
        String(b.field_label || b.field_name)
      )
    );
  }, [allFields]);

  const fieldLabelByName = useMemo(() => {
    const m = new Map();
    m.set("ownerid", "Record owner (ownerid)");
    for (const f of userLookupFields) {
      m.set(f.field_name, f.field_label || f.field_name);
    }
    return m;
  }, [userLookupFields]);

  return (
    <>
      <Row>
        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>
              Title
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Use merge fields like <code>{"{{name}}"}</code> from the
                    current record.
                  </Tooltip>
                }
              >
                <i className="fa fa-info-circle ms-2 text-muted" />
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              value={config?.title || ""}
              onChange={(e) => onChange("title", e.target.value)}
              disabled={readOnly}
              placeholder='e.g. A record {{name}} was assigned to you'
              required
            />
          </Form.Group>
        </Col>
        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Message (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={config?.message || ""}
              onChange={(e) => onChange("message", e.target.value)}
              disabled={readOnly}
              placeholder="Optional body. Same merge field syntax as title."
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">
          Recipients
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                Use the row above to add a user or a field. Selected
                recipients appear below, left to right, in the order you added
                them.
              </Tooltip>
            }
          >
            <i className="fa fa-info-circle ms-2 text-muted" />
          </OverlayTrigger>
        </Form.Label>

        <div className="border rounded p-3 bg-light text-start">
          {!readOnly && (
            <div className="d-flex flex-wrap align-items-end gap-3 gap-lg-4 mb-3 w-100 justify-content-start">
              <div
                className="flex-grow-1"
                style={{ minWidth: "220px", maxWidth: "100%", flex: "1 1 220px" }}
              >
                <Form.Label className="small text-secondary mb-1 d-block">
                  Add user
                </Form.Label>
                <LookupLayout
                  key={lookupRemountKey}
                  objectName="user"
                  searchFields="email,firstname,lastname"
                  displayFields="firstname,lastname"
                  value=""
                  defaultLabel=""
                  onChange={(userId, userName) =>
                    addHardcodedUser(userId, userName)
                  }
                  disabled={readOnly}
                  placeholder="Search to add a user…"
                  activeUsersOnly={true}
                />
              </div>
              <div
                className="flex-grow-1"
                style={{ minWidth: "220px", maxWidth: "100%" }}
              >
                <Form.Label className="small text-secondary mb-1 d-block">
                  Add field
                </Form.Label>
                <InputGroup size="sm">
                  <Form.Select
                    id={`${idPrefix}-field-pick`}
                    value={pickFieldKey}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPickFieldKey(v);
                      if (v) addFieldToken(v);
                    }}
                  >
                    <option value="">Choose field…</option>
                    <option value="ownerid">Record owner (ownerid)</option>
                    {userLookupFields.map((f) => (
                      <option key={f.field_name} value={f.field_name}>
                        {f.field_label || f.field_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFieldSelector(true)}
                    title="Pick user lookup field"
                  >
                    <i className="fa fa-search" />
                  </Button>
                </InputGroup>
              </div>
            </div>
          )}

          <div className={readOnly ? "" : "pt-2 border-top border-light"}>
            <div className="small text-secondary mb-2">Selected</div>
            {/* Use inline block flow (not a full-width flex row) so chips stay grouped left, not spaced apart */}
            <div
              className="text-start w-100"
              style={{ lineHeight: 1.8 }}
            >
              {recipientSequence.map((item, index) => {
                if (item.kind === "user") {
                  const label =
                    item.displayName ||
                    String(item.userId).slice(0, 8) + "…";
                  return (
                    <span
                      key={`u-${item.userId}-${index}`}
                      className="d-inline-block me-2 mb-1 align-middle"
                    >
                    <Badge
                      bg="secondary"
                      className="d-flex align-items-center gap-1 py-2 px-2"
                    >
                      <span>
                        <i className="fa fa-user me-1" />
                        {label}
                      </span>
                      {!readOnly && (
                        <Button
                          variant="link"
                          className="text-white p-0 ms-1 lh-1"
                          size="sm"
                          onClick={() => removeAt(index)}
                          aria-label="Remove"
                        >
                          &times;
                        </Button>
                      )}
                    </Badge>
                    </span>
                  );
                }
                return (
                  <span
                    key={`f-${item.fieldName}-${index}`}
                    className="d-inline-block me-2 mb-1 align-middle"
                  >
                  <Badge
                    bg="info"
                    text="dark"
                    className="d-flex align-items-center gap-1 py-2 px-2"
                  >
                    <span>
                      <i className="fa fa-link me-1" />
                      {fieldLabelByName.get(item.fieldName) || item.fieldName}
                    </span>
                    {!readOnly && (
                      <Button
                        variant="link"
                        className="text-dark p-0 ms-1 lh-1"
                        size="sm"
                        onClick={() => removeAt(index)}
                        aria-label="Remove"
                      >
                        &times;
                      </Button>
                    )}
                  </Badge>
                  </span>
                );
              })}

              {recipientSequence.length === 0 && (
                <span className="text-muted small d-block">
                  {readOnly
                    ? "No recipients."
                    : "No recipients yet — add a user or a field above."}
                </span>
              )}
            </div>
          </div>
        </div>
      </Form.Group>

      <Modal
        show={showFieldSelector}
        onHide={() => setShowFieldSelector(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select a user lookup field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {objectName ? (
            <ObjectFieldSelector
              objectName={objectName}
              fieldTypeFilter="lookup"
              referenceObjectFilter="user"
              onSelect={handleFieldPicked}
            />
          ) : (
            <p className="text-muted mb-0">Select a workflow object first.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
